import {
  Box,
  ListItem,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { useState } from "react";
import CircleIcon from "@mui/icons-material/Circle";
import { useAuth } from "../contexts/AuthContext";
import { useThemeVersion } from "../contexts/ThemeContext";
import { useToast } from "../services/utils";
import {
  IconBlobList,
  IconConnectionConfigSvg,
  IconDirectionArrowSvg,
  IconNewConnectionKeys,
  IconNewFileProcessingAttributes,
  IconNewLinkedServices,
  IconNewRulesImport,
  IconNewRunInstanceSvg,
  IconResourceColumnSvg,
  IconSystemType,
  IconUserSvg,
  SimCardDownloadIconSvg,
} from "../common/utils/icons";
import { downloadFileSecurely } from "../services/fileDownloadService";
import { useAppTranslation } from "../hooks/useAppTranslation";

export default function AdminPages() {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const { isAdmin } = useAuth();
  const { uiVersion, toggleUiVersion } = useThemeVersion();
  const [showFileManagement, setShowFileManagement] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(
    new Set(),
  );

  // Define multiple log files using environment variables
  const logFiles = [
    {
      name: "log1",
      path: process.env.REACT_APP_DOWNLOAD_LOG_URL_1 || "EngineLogs/logs1.log",
    },
    {
      name: "log2",
      path:
        process.env.REACT_APP_DOWNLOAD_LOG_URL_2 || "EngineLogs/logs1.log.1",
    },
    {
      name: "log3",
      path:
        process.env.REACT_APP_DOWNLOAD_LOG_URL_3 || "EngineLogs/logs1.log.2",
    },
    {
      name: "log4",
      path:
        process.env.REACT_APP_DOWNLOAD_LOG_URL_4 || "EngineLogs/logs1.log.3",
    },
    {
      name: "log5",
      path:
        process.env.REACT_APP_DOWNLOAD_LOG_URL_5 || "EngineLogs/logs1.log.4",
    },
  ];

  const handleSecureLogDownload = async (logPath: string, fileName: string) => {
    // Prevent duplicate downloads
    if (downloadingFiles.has(fileName)) {
      return;
    }

    // Add to downloading set
    setDownloadingFiles((prev) => new Set(prev).add(fileName));

    try {
      // Convert the log path to Windows-style path for secure download
      // Environment variables contain paths like "EngineLogs/logs1.log"
      // Convert to Windows format: "EngineLogs\logs1.log"
      const windowsPath = logPath.replace(/\//g, "\\");

      // Construct the full file path for secure download
      const fullPath = `C:\\inetpub\\wwwroot\\VIS\\download-files\\${windowsPath}`;

      await downloadFileSecurely(fullPath, fileName);
    } catch (error: any) {
      console.error("Secure log download failed:", error);
      showToast(t("adminPages.toast.downloadFailed"), "error");
    } finally {
      // Remove from downloading set
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(fileName);
        return newSet;
      });
    }
  };

  return (
    <>
      <Box className="text-box-card mb-0">
        <ul className="invisible-list-url">
          <li className="icon-stroke">
            <IconResourceColumnSvg />
            <Link to={"/resource-columns/all"}>
              {t("adminPages.nav.resourceColumns")}
            </Link>
          </li>
          {/* <li>
            <IconNewLinkedServices />
            <Link to={"/linked-services"}>Linked Services</Link>
          </li>
          <li>
            <IconNewConnectionKeys />
            <Link to={"/connection-keys"}>Connection Keys</Link>
          </li> */}
          {/* <li>
            <IconNewRunInstanceSvg />
            <Link to={"/run-instance"}>{t("adminPages.nav.executionGroup")}</Link>
          </li> */}
          <li>
            <IconNewFileProcessingAttributes />
            <Link to={"/file-processing-attributes"}>
              {t("adminPages.nav.fileProcessingAttributes")}
            </Link>
          </li>
          <li>
            <IconConnectionConfigSvg />
            <Link to={"/connection-runtime-config"}>
              {t("adminPages.nav.connectionRuntimeConfig")}
            </Link>
          </li>
          <li className="import">
            <IconNewRulesImport />
            <Link to={"/domain/import-entity?type=domain"}>
              {t("adminPages.nav.domainImport")}
            </Link>
          </li>

          <li className="import">
            <IconNewRulesImport />
            <Link to={"/resource/import-entity?type=resource"}>
              {t("adminPages.nav.resourceImport")}
            </Link>
          </li>
          <li className="import">
            <IconNewRulesImport />
            <Link to={"/resource-column/import-entity?type=resource-column"}>
              {t("adminPages.nav.resourceColumnImport")}
            </Link>
          </li>
          <li className="import">
            <IconNewRulesImport />
            <Link to={"/rules/import-entity?type=rule"}>
              {t("adminPages.nav.rulesImport")}
            </Link>
          </li>
          <li className="import">
            <IconNewRulesImport />
            <Link
              to={"/research-query/import-entity?type=generic-research-query"}
            >
              {t("adminPages.nav.genericRQImport")}
            </Link>
          </li>
          <li className="import">
            <IconNewRulesImport />
            <Link
              to={
                "/comparison-research-queries/import-entity?type=comparison-research-query"
              }
            >
              {t("adminPages.nav.comparisonRQImport")}
            </Link>
          </li>
          {/* <li
            className={`import has-child ${
              showFileManagement ? "child-open" : ""
            }`}
          >
            <em onClick={() => setShowFileManagement(!showFileManagement)}>
              <IconBlobList />
              File Management
              <span className="arrow-icon">
                <IconDirectionArrowSvg />
              </span>
            </em>

            {showFileManagement && (
              <ul className={`${showFileManagement ? "opened" : ""}`}>
                <ListItem className="import">
                  <CircleIcon />
                  <Link to={"/blob-list"}>Get Blob List</Link>
                </ListItem>
                <ListItem className="import">
                  <CircleIcon />
                  <Link to={"/sftp-list"}>Get SFTP List</Link>
                </ListItem>
              </ul>
            )}
          </li> */}
          <li className="import">
            <IconSystemType />
            <Link to={"/classification"}>
              {t("adminPages.nav.classification")}
            </Link>
          </li>
          <li className="import">
            <IconSystemType />
            <Link to={"/reason"}>{t("adminPages.nav.reason")}</Link>
          </li>

          {/* Add the + icon to show log files */}
          <li
            className={`import has-child   ${showLogs ? "child-open" : ""}`}
            onClick={() => setShowLogs(!showLogs)}
          >
            <em>
              <SimCardDownloadIconSvg />
              {t("adminPages.nav.downloadLogs")}
              <span className="arrow-icon">
                <IconDirectionArrowSvg />
              </span>
            </em>

            {showLogs && (
              <ul className={`${showLogs ? "opened" : ""}`}>
                {logFiles.map((log, index) => {
                  const isDownloading = downloadingFiles.has(log.name);
                  return (
                    <ListItem
                      key={index}
                      className="import"
                      onClick={() =>
                        !isDownloading &&
                        handleSecureLogDownload(log.path, log.name)
                      }
                      style={{
                        cursor: isDownloading ? "not-allowed" : "pointer",
                        opacity: isDownloading ? 0.6 : 1,
                      }}
                    >
                      {isDownloading ? (
                        <CircularProgress
                          size={16}
                          style={{ marginRight: 8 }}
                        />
                      ) : (
                        <CircleIcon />
                      )}
                      <span>{log.name}</span>
                    </ListItem>
                  );
                })}
              </ul>
            )}
          </li>

          {/* Display the list of log files if showLogs is true */}
        </ul>
      </Box>

      {/* ── UI Theme Settings — DQ-8526 ────────────────────────────────────
           Visible only to admin users. Allows toggling between the legacy
           SCSS-based theme and the new MUI-based theme introduced by the
           DQ-8525 epic. The preference is persisted in localStorage so it
           survives page refreshes.
      ──────────────────────────────────────────────────────────────────── */}
      {isAdmin && (
        <Box className="text-box-card mb-0" sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            UI Theme Settings
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={uiVersion === "new"}
                onChange={toggleUiVersion}
                color="primary"
                inputProps={{ "aria-label": "Enable New UI theme" }}
              />
            }
            label="Enable New UI (Beta)"
          />
          <Typography
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ mt: 0.5 }}
          >
            Switch between the legacy SCSS-based theme and the new MUI-based
            theme. Changes apply immediately across all pages.
          </Typography>
        </Box>
      )}
    </>
  );
}
